/* Importing the necessary libraries and files */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router-dom';

import moment from 'moment';
import PropTypes from 'prop-types';

/* Importing the components from the Material UI library. */
import { Breadcrumbs, Button, Container, Grid, Link, Stack, Typography } from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon } from '@mui/icons-material';

/* Importing the getPatientAll and deletePatient functions from the patientSlice.js file. */
import { deletePatient, getPatientAll } from '../../features/patient/patientSlice';

/* Importing the components from the components folder. */
import DateTimePatientCards from '../../components/DateTimePatientCards';
import MaterialTable from '../../components/MaterialTable';
import Page from '../../components/Page';

/* Importing function from the utils folder. */
import deleteResourceHandler from '../../utils/deleteResourceHandler';
import { fPatients } from '../../utils/Patient/Patient.utils.format';

/* Defining the propTypes for the PatientList component. */
PatientList.propTypes = {
  isCovid19Page: PropTypes.bool,
};

/**
 * Renders the Patient List page.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {boolean} props.isCovid19Page - Indicates if the page is for COVID-19 patients.
 * @returns {JSX.Element} The rendered Patient List page.
 */
export default function PatientList(props) {
  const { isCovid19Page } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* Using the useSelector hook to get the patient state from the Redux store. */
  const { Patients } = useSelector((state) => state.patient);

  /* Setting the states of objects. */
  const [isLoading, setIsLoading] = useState(true);
  const [PatientsFormatted, setPatientFormatted] = useState([]);
  const [title, setTitle] = useState('Patient List');

  const formatPatients = useCallback(() => {
    const patientsArray = fPatients(Patients);
    setPatientFormatted(patientsArray);
    setIsLoading(false);
  }, [Patients]);

  const initializeData = useCallback(async () => {
    setTitle(isCovid19Page ? 'COVID-19 Patients' : 'Patient List');
    dispatch(getPatientAll());
  }, [dispatch, isCovid19Page]);

  /* Calling the initializeData function when the component mounts. */
  useEffect(() => {
    initializeData();
  }, [initializeData, isCovid19Page]);

  /* Calling the formatPatients function when the component mounts and when the patientState changes. */
  useEffect(() => {
    formatPatients();
  }, [formatPatients]);

  const updatePatientHandler = (_id) => {
    navigate(`/patient/edit/${_id}`, { replace: false });
  };

  const deletePatientHandler = (id) => {
    deleteResourceHandler({
      dispatch,
      deleteAction: deletePatient,
      id,
      successMessage: 'Patient deleted successfully!',
    });
  };

  /* Creating a table with the following columns: ID, Last Name, Given Name, Suffix, Gender, and
  Birthday. */
  const columns = [
    {
      title: 'ID',
      field: 'id',
    },
    {
      title: 'Last Name',
      field: 'name_family',
      // eslint-disable-next-line consistent-return
      customFilterAndSearch: (value, rowData) => {
        const fullNameReverse = `${rowData.name_family} ${rowData.name_given}`;
        const fullName = `${rowData.name_given} ${rowData.name_family}`;
        if (
          rowData.name_family.toLowerCase().includes(value.toLowerCase()) ||
          fullName.toLowerCase().includes(value.toLowerCase()) ||
          fullNameReverse.toLowerCase().includes(value.toLowerCase())
        ) {
          return rowData;
        }
      },
    },
    {
      title: 'Given Name',
      field: 'name_given',
    },
    {
      title: 'Suffix',
      field: 'name_suffix',
    },
    {
      title: 'Gender',
      field: 'gender',
    },
    {
      title: 'Birthday',
      field: 'birthDate',
    },
  ];

  /* Creating a table with the columns ID, Last Name, Given Name, Suffix, Gender, Birthday, COVID-19
  Case, and COVID-19 Diagnosis. */
  const columnsCovid19 = [
    {
      title: 'ID',
      field: 'id',
    },
    {
      title: 'Last Name',
      field: 'name_family',
      // eslint-disable-next-line consistent-return
      customFilterAndSearch: (value, rowData) => {
        const fullNameReverse = `${rowData.name_family} ${rowData.name_given}`;
        const fullName = `${rowData.name_given} ${rowData.name_family}`;
        if (
          rowData.name_family.toLowerCase().includes(value.toLowerCase()) ||
          fullName.toLowerCase().includes(value.toLowerCase()) ||
          fullNameReverse.toLowerCase().includes(value.toLowerCase())
        ) {
          return rowData;
        }
      },
    },
    {
      title: 'Given Name',
      field: 'name_given',
    },
    {
      title: 'Suffix',
      field: 'name_suffix',
    },
    {
      title: 'Gender',
      field: 'gender',
    },
    {
      title: 'Birthday',
      field: 'birthDate',
    },
    // {
    //   title: "COVID-19 Case",
    //   field: "Covid Case",
    //   render: (rowData) => `${rowData["Covid Case"] || ""}`,
    // },
    // {
    //   title: "COVID-19 Diagnosis",
    //   field: "classification",
    //   render: (rowData) => `${rowData.classification || ""}`,
    // },
  ];

  /**
   * Render the table of patients.
   */
  const renderTable = () => (
    <MaterialTable
      onRowClick={(event, rowData) => {
        navigate(`/patient/details/${rowData.id}`, { replace: false });
      }}
      options={{
        search: true,
        exportButton: true,
        exportFileName: moment().format('YYYY-MM-DD HH:mm:ss'),
        actionsColumnIndex: -1,
        // searchFieldStyle: { variant:"outlined" }
      }}
      actions={[
        {
          icon: () => <EditIcon />,
          tooltip: 'Edit Patient',
          onClick: (event, rowData) => updatePatientHandler(rowData.id),
        },
        {
          icon: () => <DeleteIcon />,
          tooltip: 'Delete Patient',
          onClick: (event, rowData) => deletePatientHandler(rowData.id),
        },
      ]}
      title={''}
      isLoading={isLoading}
      columns={isCovid19Page ? columnsCovid19 : columns}
      data={PatientsFormatted}
      // components={{
      //   Toolbar: props => (
      //     <Grid container sx={{ p: 2 }}>
      //       <Grid item xs={12} md={columnSize.dropDowns}>
      //         <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
      //           <TextField
      //             fullWidth
      //             select
      //             value={filter.admissionStatus || ""}
      //             name="admissionStatus"
      //             onChange={(e) => {
      //               const data = { ...filter };
      //               data[e.target.name] = e.target.value;
      //               setFilter(data);
      //               if (e.target.value) {
      //                 setSearchParams({ ...searchParams, admissionStatus: e.target.value });
      //               } else {
      //                 setSearchParams({ ...searchParams, admissionStatus: "" });
      //               }
      //             }}
      //             label="Admission Status"
      //           >
      //             <MenuItem value="">
      //               <em>None</em>
      //             </MenuItem>
      //             {statusCode.admissionStatus.map((el) => <MenuItem key={el.rps_name} value={el.rps_name}>{el.rps_name}</MenuItem>)}
      //           </TextField>

      //           { isCovid19Page
      //             ? <>
      //               <TextField
      //                 fullWidth
      //                 select
      //                 value={filter.covidStatus || ""}
      //                 name="covidStatus"
      //                 onChange={(e) => {
      //                   const data = { ...filter };
      //                   data[e.target.name] = e.target.value;
      //                   setFilter(data);
      //                   // if (e.target.value) {
      //                   //   setSearchParams({ ...searchParams, covidStatus: e.target.value });
      //                   // } else {
      //                   //   setSearchParams({ ...searchParams, covidStatus: "" });
      //                   // }
      //                 }}
      //                 label="COVID-19 Case"
      //               >
      //                 <MenuItem value="">
      //                   <em>None</em>
      //                 </MenuItem>
      //                 {statusCode.covidCase.map((el) => <MenuItem key={el.rps_name} value={el.rps_name}>{el.rps_name}</MenuItem>)}
      //               </TextField>

      //               <TextField
      //                 fullWidth
      //                 select
      //                 value={filter.classificationStatus || ""}
      //                 name="classificationStatus"
      //                 onChange={(e) => {
      //                   const data = { ...filter };
      //                   data[e.target.name] = e.target.value;
      //                   setFilter(data);
      //                   // console.log(...searchParams)
      //                   // if (e.target.value) {

      //                   //   setSearchParams({ ...searchParams, classificationStatus: e.target.value });
      //                   // } else {
      //                   //   setSearchParams({ ...searchParams, classificationStatus: "" });
      //                   // }
      //                 //   const newObj = {};

      //                 //   Object.keys(searchParams).forEach((key) => {
      //                 //     if (key === classificationStatus) {
      //                 //       newObj.classificationStatus = e.target.value || "";
      //                 //     } else {
      //                 //       newObj[key] = searchParams[key];
      //                 //     }
      //                 //   });
      //                 //   console.log(newObj);
      //                 //   setSearchParams(newObj);
      //                 }}
      //                 label="COVID-19 Diagnosis"
      //               >
      //                 <MenuItem value="">
      //                   <em>None</em>
      //                 </MenuItem>
      //                 {statusCode.classification.map((el) => <MenuItem key={el.rps_name} value={el.rps_name}>{el.rps_name}</MenuItem>)}
      //               </TextField>

      //             </>
      //             : <></>
      //           }

      //         </Stack>
      //       </Grid>

      //       <Grid item xs={12} md={columnSize.searchField} >
      //         <MTableToolbar {...props} />
      //       </Grid>

      //     </Grid>
      //   ),
      // }}
    />
  );

  return (
    <Page title={title}>
      <Container maxWidth="none">
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Stack direction="column" justifyContent="space-between">
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>
            <Breadcrumbs aria-label="breadcrumb" mr={4}>
              {isCovid19Page && (
                <Link underline="hover" color="inherit" component={RouterLink} to="/covid19/dashboard">
                  COVID-19
                </Link>
              )}
              <Typography color="text.primary">Patient</Typography>
              {/* <Typography color="text.primary">List</Typography> */}
            </Breadcrumbs>
          </Stack>

          <Button variant="contained" component={RouterLink} to="/patient/register" startIcon={<AddIcon />}>
            Add Patient
          </Button>
        </Stack>

        <DateTimePatientCards isCovid19Page={isCovid19Page} />

        <Grid container>
          <Grid item xs={12}>
            {renderTable()}
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
}
